/*
Author: Jeewantha Madubashana Karunarathna
Date: 07/04/2025
Copyright © 2025 Infix!. All rights reserved.
*/

import { useDownloadInformationPackFile, useGetInformationPackForWaitingListRecord, useGetInformationPacks, useGetInformationPacksDetails } from '@/state/survey/poaSurveySlice';
import { memo, useEffect, useRef, useState } from "react";
import { RootState } from "@/reducers/store";
import { useSelector } from "react-redux";
import InformationPackHtmlContent from "@/components/Atoms/InformationPackHtmlContent";
import { INode, Ioption } from "../../SurveyModel/types/nodeTypes";
import ValidationManager from "../../SurveyModel/fieldValidations";
import DropDownNode from "../DropDown/DropDownNode";
import { useAddNewField, useValidate } from "@/state/survey/formSlice";
import fileIcon from '@/assets/fileIcon.svg';
import { useParams } from 'react-router-dom';

const InstructionsAndInformation: React.FC = memo(() => {
    const getInformationPacks = useGetInformationPacks();
    const updateInformationPackDetails = useGetInformationPacksDetails();
    const downloadInformationPackFile = useDownloadInformationPackFile();
    const getInformationPackForWaitingListRecord = useGetInformationPackForWaitingListRecord();
    const { informationPacks, informationPackDetails, selectedInformationPack, isLoading } = useSelector((state: RootState) => state.poaSurvey);
    const form = useSelector((state: RootState) => state.form);
    const [selectedInformationPackDetails, setSelectedInformationPackDetails] = useState<InformationPackOption | null>(null);
    const [node, setNode] = useState<INode | undefined>();
    const addNewFieldValue = useAddNewField();
    const { waitingListRecordId } = useParams();
    const validateInputField = useValidate();

    const hasInitialized = useRef(false);
    const hasFetchedRecord = useRef(false);

    interface InformationPackOption {
        id: number;
        title: string;
        description: string;
        files: {
            id: number;
            informationPackId: number;
            fileUrl: string;
            fileName: string
        }[];
        links: {
            id: number;
            title: string;
            url: string;
        }[];
    }

    interface IFile {
        id: number,
        informationPackId: number
        fileName: string
    }

    interface InformationPack {
        id: number;
        informationPackId: string;
        title?: string
    }

    useEffect(() => {
        getInformationPacks();
        hasInitialized.current = true;
        return () => {
            setNode(undefined);
            setSelectedInformationPackDetails(null);
            hasInitialized.current = false; 
        };
    }, []);

    useEffect(() => {
        if (Array.isArray(informationPacks) && informationPacks.length > 0 ) {
            if (!node) {
                initializeInformationPackNode();
            }
            if (waitingListRecordId && !hasFetchedRecord.current) {
                getInformationPackForWaitingListRecord(waitingListRecordId);
                hasFetchedRecord.current = true;
              }
        }
    }, [informationPacks, waitingListRecordId]);

    useEffect(() => {
        if(node && !form['InformationPackName']){
            const detailsNodeForm = ValidationManager.applyCommonValidationsWithValue(node, '');
            addNewFieldValue(node.name, detailsNodeForm);
        }
    }, [node]);

    useEffect(() => {
        const selectedValue = form['InformationPackName'];
        const selectedPack = (informationPacks as InformationPack[])?.find(
            (informationPack) => informationPack.title === selectedValue?.value
        );
        if (selectedPack) {
            updateInformationPackDetails(selectedPack.id);
        }
    }, [form['InformationPackName']]);

    useEffect(() => {
        if (selectedInformationPack && selectedInformationPack.informationPackId && form.hasOwnProperty('InformationPackName') && node) {
            const selectedPack = (informationPacks as InformationPack[])?.find(
                (pack) => pack.id === selectedInformationPack.informationPackId
            );
            validateInputField(node.name, selectedPack?.title, true);
        }
    }, [selectedInformationPack, node]);

    useEffect(() => {
        if (informationPackDetails && !Array.isArray(informationPackDetails)) {
            setSelectedInformationPackDetails(informationPackDetails);
        } else {
            setSelectedInformationPackDetails(null);
        }
    }, [informationPackDetails]);

    const initializeInformationPackNode = () => {
        if (
            Array.isArray(informationPacks) &&
            informationPacks.length > 0 &&
            !node 
        ) {
            const packOptions: Ioption[] = informationPacks.map((pack: any) => ({
                name: pack.title,
                description: '',
            }));

            const InstructionAndInformationNode: INode = {
                options: packOptions,
                title: 'Information pack',
                subTitle: '',
                disabled: false,
                validations: [
                    {
                        type: 'required',
                        message: 'Mandatory field',
                        field: '',
                    },
                ],
                configurations: [
                    { key: 'preventUpdate', value: 'false' },
                    { key: 'FieldWidthPct', value: '0.5' },
                    { key: 'outsideQuestionnaire', value: 'true' }
                ],
                type: 'SingleSelect',
                alerts: [],
                validationDependants: [],
                name: 'InformationPackName',
                showIf: '',
                nodes: []
            };
            setNode(InstructionAndInformationNode);
        }
    };

    const downloadFile = (file: IFile) => {
        downloadInformationPackFile(file.id, file.informationPackId, file.fileName);
    };

    return (
        <div className="instruction-and-info-wrapper">
            
            {node && form && form['InformationPackName']?.value &&
                <DropDownNode
                    node={node}
                    children={undefined}
                    name={node.name}
                    disabled={false}
                    onBlur={() => { }}
                />
            }

            {!isLoading && node && form['InformationPackName'] && selectedInformationPackDetails?.description &&
                <div className="information-pack-description-wrap">
                    <InformationPackHtmlContent
                        htmlContent={selectedInformationPackDetails?.description}
                    />
                </div>
            }


            {!isLoading && node && form['InformationPackName'] && selectedInformationPackDetails?.links &&
                <div className="information-pack-description-links-wrap" >
                    <div className='title'>Links</div>
                    {
                        selectedInformationPackDetails.links.map((link) => {
                            return (
                                <a href={link.url} target='_blank' className='link'>{link.title}</a>
                            )
                        })
                    }
                </div>
            }

            {
                !isLoading && node && form['InformationPackName'] && selectedInformationPackDetails?.files &&
                <div className="information-pack-description-files-wrap">
                    <div className='title'>Files</div>
                    {
                        selectedInformationPackDetails.files.map((file) => {
                            return (
                                <span onClick={() => { downloadFile(file) }} className='file-url'> <img src={fileIcon} ></img>  <p>{file.fileName ? file.fileName : "File Name not available"}</p></span>
                            )
                        })
                    }
                </div>
            }
        </div>

    );
})

export default InstructionsAndInformation;



